<template>
  <div class="-wt-mass-produce-preview">
    <div class="-info">
      <div class="-desc">
        Watch the sample video created for this record. If it’s ok, click
        continue.
      </div>
      <div class="-data-table">
        <div class="-row -header">
          <div class="-col">Parameter Name</div>
          <div class="-col">Value</div>
        </div>
        <div class="-row" v-for="(value, field) in filteredRow" :key="field">
          <div class="-col" v-text="field" />
          <div class="-col" v-text="value" />
        </div>
      </div>
    </div>
    <div class="-preview">
      <generatingState v-if="isGenerating" />

      <playableState
        v-else-if="isPlayable"
        :previewState="previewState"
        :storyboard="storyboard"
      />

      <errorState v-else-if="isFailed" @generate-preview="generatePreview" />
    </div>
  </div>
</template>

<script>
import playableState from './preview/wtMassProduce.preview.playableState.vue';
import generatingState from './preview/wtMassProduce.preview.generatingState.vue';
import errorState from './preview/wtMassProduce.preview.errorState.vue';

const defaultPreviewState = () => {
  return {
    state: 'generating',
    videoId: null,
    videoUrl: null,
    parts: [],
  };
};

export default {
  components: {
    playableState,
    generatingState,
    errorState,
  },
  data() {
    return {
      states: {
        GENERATING: 'generating',
        PLAYABLE: 'playable',
        FAILED: 'failed',
      },
      previewState: {},
      previewPartsResource: null,
      previewTaskResource: null,
    };
  },

  props: {
    storyboard: {
      type: Object,
      required: true,
    },
    previewRow: {
      type: Object,
      required: true,
    },
  },

  created() {},

  mounted() {
    this.generatePreview();
  },

  beforeDestroy() {
    this.resetPreviewState();
  },

  methods: {
    generatePreview() {
      this.resetPreviewState();
      const linkedFields = Object.keys(this.filteredRow).map((key) => ({
        key,
        value: this.filteredRow[key],
      }));
      app.api.renderApi
        .videoByTemplate({
          templateId: this.storyboard.id,
          createPreview: true,
          videoUpdates: {
            linkedFields,
          },
        })
        .then((res) => {
          this.setPreviewState({
            state: this.states.GENERATING,
            videoId: res.videoId,
            previewId: res.previewId,
            videoUrl: res.videoUrl,
            duration: res.duration,
          });
          this.awaitPreview();
        })
        .catch((e) => {
          this.setPreviewState({ state: this.states.FAILED });
        });
    },

    awaitPreview(payload) {
      const previewId = this.previewState.previewId;
      const videoId = this.previewState.videoId;
      const userId = app.rootStore.state.user['employeeId'];
      this.previewPartsResource = `/users/${userId}/stories/${videoId}/preview_parts/${previewId}/parts`;
      this.previewTaskResource = `/users/${userId}/stories/${videoId}/previews/${previewId}`;

      app.api.realTime.listenOn({
        path: this.previewPartsResource,
        callback: ({ val }) => {
          const goodResponse = !_.some(val, function (part) {
            return 'FAILED' === part.message;
          });
          if (goodResponse) {
            const aKeys = Object.keys(val);
            if (aKeys.length > 1 && _.includes(aKeys, 'AUDIO')) {
              this.setPreviewState({ state: this.states.PLAYABLE, parts: val });
            }
          }
        },
      });

      app.api.realTime.listenOn({
        path: this.previewTaskResource,
        callback: ({ val }) => {
          if (val.message !== 'DONE') {
            this.setPreviewState({ state: this.states.FAILED });
          }
          app.api.realTime.cancelListen(this.previewPartsResource);
          app.api.realTime.cancelListen(this.previewTaskResource);
        },
      });
    },

    resetPreviewState() {
      this.$set(this, 'previewState', defaultPreviewState());
      if (this.previewPartsResource) {
        app.api.realTime.cancelListen(this.previewPartsResource);
        this.previewPartsResource = null;
      }
      if (this.previewTaskResource) {
        app.api.realTime.cancelListen(this.previewTaskResource);
        this.previewPartsResource = null;
      }
    },

    setPreviewState(previewState) {
      this.$set(
        this,
        'previewState',
        Object.assign(this.previewState, previewState)
      );
    },
  },
  computed: {
    filteredRow() {
      return _.omit(this.previewRow, ['___wochitKey', '__parsed_extra']);
    },
    isGenerating() {
      return this.previewState.state === this.states.GENERATING;
    },

    isPlayable() {
      return this.previewState.state === this.states.PLAYABLE;
    },

    isFailed() {
      return this.previewState.state === this.states.FAILED;
    },
  },
};
</script>

<style lang="scss" scoped>
.-wt-mass-produce-preview {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 400px;

  > div {
    height: 369px;
  }

  .-info {
    overflow: auto;
    margin: 0 20px 0 0;
    padding: 0 10px 0 0;
    flex-grow: 1;
    flex-shrink: 1;
    .-desc {
      max-width: 500px;
      margin-bottom: 20px;
    }
    .-data-table {
      display: flex;
      flex-direction: column;
      border: 1px solid #d9d9d9;
      .-row {
        display: flex;
        flex-direction: row;
        &.-header {
          font-weight: 500;
        }
        &:not(:last-of-type) {
          border-bottom: 1px solid #d9d9d9;
        }
        .-col {
          flex: 1;
          padding: 10px 15px;
          word-break: break-all;
          &:not(:last-of-type) {
            border-right: 1px solid #d9d9d9;
          }
        }
      }
    }
  }

  .-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #4a4a4a;
    font-size: 28px;
    color: #ffffff;
    font-weight: 300;
    flex-shrink: 0;
    flex-grow: 0;
    width: 656px;
    height: 369px;
    outline: 1px solid rgba(0, 0, 0, 0.1);
  }
}
</style>
